import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Search,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Stethoscope,
  Eye,
  EyeOff,
  BadgeCheck,
  ShieldCheck,
  ExternalLink,
  Ban,
  RefreshCcw,
  FileSignature,
} from "lucide-react";

const PER_PAGE = 15;

export default function AdminMedicos() {
  const { id } = useParams();
  if (id) return <MedicoDetalle userId={id} />;
  return <MedicosLista />;
}

function MedicosLista() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/admin/medicos?${params}`);
      setRows(data.items);
      setTotal(data.meta.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-medicos-lista">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F2942]">Médicos</h2>
          <p className="text-xs text-muted-foreground">{total} médicos registrados</p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            load();
          }}
          className="relative w-full sm:w-72"
        >
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o correo…"
            className="pl-9"
            data-testid="admin-medicos-search"
          />
        </form>
      </div>

      <div className="mt-5 rounded-xl border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No hay médicos con esos filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Médico</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Especialidad</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Ubicación</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Suscripción</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Visible</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.user_id}
                    className="border-t border-border hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/admin/medicos/${r.user_id}`)}
                    data-testid={`medico-row-${r.user_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{r.nombre_completo}</div>
                      <div className="text-[11px] text-muted-foreground">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-foreground">{r.especialidad || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {r.ciudad || "—"}
                      {r.estado ? `, ${r.estado}` : ""}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.subscription_status ? (
                        <span className="capitalize">
                          {r.subscription_status.replace("_", " ")}
                          {r.subscription_plan ? ` · ${r.subscription_plan}` : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Sin suscripción</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.is_visible_directory ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <Eye size={12} /> En directorio
                        </span>
                      ) : r.admin_hidden ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                          <EyeOff size={12} /> Oculto (admin)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                          <XCircle size={12} /> No visible
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-[#2563EB] text-xs font-medium">Ver</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Detail ----------------

const emptyPerfil = {
  nombre_completo: "",
  especialidad: "",
  especialidad_otra: "",
  cedula_profesional: "",
  cedula_especialidad: "",
  telefono: "",
  correo_contacto: "",
  cofepris_aviso: "",
  direccion: { calle: "", colonia: "", ciudad: "", estado: "", cp: "" },
};

function MedicoDetalle({ userId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = !!user?.is_superadmin;
  const [detail, setDetail] = useState(null);
  const [catalogos, setCatalogos] = useState({ especialidades: [], estados: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyPerfil);
  const [previewingDoc, setPreviewingDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, cat] = await Promise.all([
        api.get(`/admin/medicos/${userId}`),
        api.get("/admin/catalogos"),
      ]);
      setDetail(d.data);
      setCatalogos(cat.data);
      const p = d.data.perfil;
      setForm({
        nombre_completo: p.nombre_completo || d.data.user.nombre_completo || "",
        especialidad: p.especialidad || "",
        especialidad_otra: p.especialidad_otra || "",
        cedula_profesional: p.cedula_profesional || "",
        cedula_especialidad: p.cedula_especialidad || "",
        telefono: p.telefono || "",
        correo_contacto: p.correo_contacto || d.data.user.email || "",
        cofepris_aviso: p.cofepris_aviso || "",
        direccion: { ...emptyPerfil.direccion, ...(p.direccion || {}) },
      });
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 404) navigate("/admin/medicos", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const savePerfil = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cedula_especialidad: (form.cedula_especialidad || "").trim(),
      };
      await api.put(`/admin/medicos/${userId}/perfil`, payload);
      toast.success("Perfil del médico actualizado.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/medicos/${userId}/cuenta`, {
        is_active: !detail.user.is_active,
      });
      setDetail(data);
      toast.success(data.user.is_active ? "Cuenta reactivada." : "Cuenta desactivada.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/medicos/${userId}/cuenta`, {
        admin_hidden: !detail.perfil.admin_hidden,
      });
      setDetail(data);
      toast.success(
        data.perfil.admin_hidden ? "Médico oculto del directorio." : "Médico visible nuevamente."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibleOverride = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/medicos/${userId}/cuenta`, {
        admin_visible_override: !detail.perfil.admin_visible_override,
      });
      setDetail(data);
      toast.success(
        data.perfil.admin_visible_override
          ? "Visibilidad forzada activada. El médico aparecerá en el directorio."
          : "Visibilidad forzada desactivada."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !detail) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando médico…
      </div>
    );
  }

  const u = detail.user;
  const p = detail.perfil;
  const sub = p.subscription || {};

  return (
    <div data-testid="admin-medico-detalle" className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/medicos")}>
          <ArrowLeft size={14} /> Regresar a médicos
        </Button>
      </div>

      {p?.acuerdo && (
        <section
          className={`rounded-xl border p-4 flex items-start gap-3 ${
            p.acuerdo.firmado
              ? "border-emerald-200 bg-emerald-50"
              : "border-amber-200 bg-amber-50"
          }`}
          data-testid="medico-acuerdo-card"
        >
          <div
            className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${
              p.acuerdo.firmado
                ? "bg-emerald-100 text-emerald-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            <FileSignature size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`text-[11px] uppercase tracking-widest font-semibold ${
                p.acuerdo.firmado ? "text-emerald-800" : "text-amber-800"
              }`}
            >
              Acuerdo de Uso · v{p.acuerdo.version_actual || "1.0"}
            </div>
            <div
              className="text-sm text-foreground mt-0.5"
              data-testid="medico-acuerdo-status"
            >
              {p.acuerdo.firmado ? (
                <>
                  <b>Firmado</b> el{" "}
                  {new Date(p.acuerdo.fecha).toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}{" "}
                  como <span className="italic">"{p.acuerdo.nombre_firmado}"</span>.
                </>
              ) : (
                <><b>Pendiente de firma.</b> Verá el gate al iniciar sesión.</>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-border bg-white p-6" data-testid="medico-cuenta">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#0F2942] text-white grid place-items-center">
              <Stethoscope size={20} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-[#2563EB]">
                Médico
              </div>
              <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-0.5">
                {u.nombre_completo}
              </h2>
              <div className="text-sm text-muted-foreground">{u.email}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <StatusPill on={u.is_active}>
                  {u.is_active ? "Cuenta activa" : "Cuenta desactivada"}
                </StatusPill>
                <StatusPill on={p.perfil_completo}>
                  {p.perfil_completo ? "Perfil completo" : "Perfil incompleto"}
                </StatusPill>
                <StatusPill on={p.is_visible_directory}>
                  {p.is_visible_directory ? "Visible en directorio" : "No visible"}
                </StatusPill>
                {p.admin_hidden && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800">
                    <EyeOff size={10} /> Oculto por admin
                  </span>
                )}
                {p.admin_visible_override && !p.admin_hidden && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-indigo-300 bg-indigo-50 text-indigo-800"
                    data-testid="medico-badge-override"
                  >
                    <Eye size={10} /> Visibilidad forzada
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleHidden}
              disabled={saving}
              data-testid="medico-toggle-hidden"
            >
              {p.admin_hidden ? (
                <>
                  <Eye size={14} /> Restaurar en directorio
                </>
              ) : (
                <>
                  <EyeOff size={14} /> Ocultar del directorio
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleVisibleOverride}
              disabled={saving || p.admin_hidden}
              data-testid="medico-toggle-visible-override"
              title={p.admin_hidden ? "Primero quita 'Oculto por admin'." : ""}
              className={
                p.admin_visible_override
                  ? "border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  : ""
              }
            >
              <Eye size={14} />
              {p.admin_visible_override
                ? "Quitar visibilidad forzada"
                : "Forzar visibilidad en directorio"}
            </Button>
            {u.is_active ? (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={toggleActive}
                disabled={saving}
                data-testid="medico-toggle-active"
              >
                <Ban size={14} /> Desactivar
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={toggleActive}
                disabled={saving}
                className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                data-testid="medico-toggle-active"
              >
                <RefreshCcw size={14} /> Reactivar
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* SUSCRIPCION (solo lectura) */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="medico-suscripcion"
      >
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-[#2563EB]" />
          <h3 className="font-display text-base font-semibold text-[#0F2942]">
            Suscripción (solo lectura)
          </h3>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 text-sm">
          <KV k="Status" v={sub.status || "—"} />
          <KV k="Plan" v={sub.plan || "—"} />
          <KV
            k="Renovación"
            v={
              sub.current_period_end
                ? new Date(sub.current_period_end).toLocaleDateString("es-MX")
                : "—"
            }
          />
          <KV
            k="Cancela al fin de periodo"
            v={sub.cancel_at_period_end ? "Sí" : "No"}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-border grid sm:grid-cols-2 gap-3 text-sm" data-testid="medico-whatsapp-stats">
          <KV k="Clicks a WhatsApp (total)" v={p.whatsapp_clicks ?? 0} />
          <KV
            k="Último click"
            v={
              p.whatsapp_last_click_at
                ? new Date(p.whatsapp_last_click_at).toLocaleString("es-MX")
                : "—"
            }
          />
        </div>
      </section>

      {/* DOCUMENTOS */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="medico-documentos"
      >
        <div className="flex items-center gap-2">
          <BadgeCheck size={16} className="text-[#2563EB]" />
          <h3 className="font-display text-base font-semibold text-[#0F2942]">
            Documentos profesionales
          </h3>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
          {["titulo_profesional", "cedula_profesional", "cedula_especialidad", "cv"].map(
            (tipo) => {
              const d = p.documentos?.[tipo];
              return (
                <div
                  key={tipo}
                  className={`rounded-md border ${d ? "border-emerald-200 bg-emerald-50/40" : "border-border bg-muted/30"} px-3 py-2 flex items-center justify-between`}
                  data-testid={`admin-doc-${tipo}`}
                >
                  <div>
                    <div className="text-xs font-semibold text-foreground capitalize">
                      {tipo.replace(/_/g, " ")}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {d
                        ? `Subido ${new Date(d.uploaded_at).toLocaleDateString("es-MX")} · ${(d.size / 1024).toFixed(1)} KB`
                        : "No cargado"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {d && isSuperadmin && (
                      <SuperadminDocBtn medicoUserId={userId} docId={d.id} tipo={tipo} />
                    )}
                    <div className="text-[11px] text-muted-foreground italic">
                      {d ? "Privado" : "—"}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Los documentos siguen siendo privados: son de verificación, no de directorio.
          El propietario médico puede descargarlos con URL firmada de 5 min. Solo superadmin
          puede abrirlos aquí con auditoría reforzada.
        </p>
      </section>

      {/* PERFIL EDIT */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="medico-perfil-edit"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              Editar perfil COFEPRIS
            </div>
            <h3 className="font-display text-base font-semibold text-[#0F2942] mt-0.5">
              Cambios entran en vigor de inmediato en el directorio público
            </h3>
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Nombre completo">
            <Input
              value={form.nombre_completo}
              onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })}
              data-testid="admin-medico-nombre"
            />
          </Field>
          <Field label="Correo de contacto">
            <Input
              type="email"
              value={form.correo_contacto}
              onChange={(e) => setForm({ ...form, correo_contacto: e.target.value })}
              data-testid="admin-medico-correo"
            />
          </Field>
        </div>

        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <Field label="Especialidad">
            <Select
              value={form.especialidad}
              onValueChange={(v) => setForm({ ...form, especialidad: v })}
            >
              <SelectTrigger data-testid="admin-medico-especialidad">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(catalogos.especialidades || []).map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Teléfono (10 dígitos)">
            <Input
              value={form.telefono}
              onChange={(e) =>
                setForm({ ...form, telefono: e.target.value.replace(/\D/g, "").slice(0, 10) })
              }
              data-testid="admin-medico-telefono"
              inputMode="numeric"
            />
          </Field>
        </div>

        {form.especialidad === "Otra" && (
          <div className="mt-3">
            <Field label="Describe la especialidad">
              <Input
                value={form.especialidad_otra}
                onChange={(e) => setForm({ ...form, especialidad_otra: e.target.value })}
                data-testid="admin-medico-esp-otra"
              />
            </Field>
          </div>
        )}

        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <Field label="Cédula profesional (7-8 dígitos)">
            <Input
              value={form.cedula_profesional}
              onChange={(e) =>
                setForm({
                  ...form,
                  cedula_profesional: e.target.value.replace(/\D/g, "").slice(0, 8),
                })
              }
              data-testid="admin-medico-cedula"
              inputMode="numeric"
            />
          </Field>
          <Field label="Cédula de especialidad (opcional)">
            <Input
              value={form.cedula_especialidad}
              onChange={(e) =>
                setForm({
                  ...form,
                  cedula_especialidad: e.target.value.replace(/\D/g, "").slice(0, 8),
                })
              }
              data-testid="admin-medico-cedula-esp"
              inputMode="numeric"
            />
          </Field>
        </div>

        <div className="mt-3">
          <Field label="Aviso COFEPRIS">
            <Input
              value={form.cofepris_aviso}
              onChange={(e) => setForm({ ...form, cofepris_aviso: e.target.value })}
              data-testid="admin-medico-cofepris"
            />
          </Field>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Calle">
            <Input
              value={form.direccion.calle}
              onChange={(e) =>
                setForm({ ...form, direccion: { ...form.direccion, calle: e.target.value } })
              }
              data-testid="admin-medico-calle"
            />
          </Field>
          <Field label="Colonia">
            <Input
              value={form.direccion.colonia}
              onChange={(e) =>
                setForm({ ...form, direccion: { ...form.direccion, colonia: e.target.value } })
              }
              data-testid="admin-medico-colonia"
            />
          </Field>
        </div>
        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <Field label="Ciudad">
            <Input
              value={form.direccion.ciudad}
              onChange={(e) =>
                setForm({ ...form, direccion: { ...form.direccion, ciudad: e.target.value } })
              }
              data-testid="admin-medico-ciudad"
            />
          </Field>
          <Field label="Estado">
            <Select
              value={form.direccion.estado}
              onValueChange={(v) =>
                setForm({ ...form, direccion: { ...form.direccion, estado: v } })
              }
            >
              <SelectTrigger data-testid="admin-medico-estado">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(catalogos.estados || []).map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Código postal">
            <Input
              value={form.direccion.cp}
              onChange={(e) =>
                setForm({
                  ...form,
                  direccion: { ...form.direccion, cp: e.target.value.replace(/\D/g, "").slice(0, 5) },
                })
              }
              data-testid="admin-medico-cp"
              inputMode="numeric"
            />
          </Field>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          {p.is_visible_directory && (
            <a
              href={`/directorio/${p.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#2563EB] inline-flex items-center gap-1"
              data-testid="medico-ver-ficha"
            >
              Ver ficha pública <ExternalLink size={12} />
            </a>
          )}
          <Button
            onClick={savePerfil}
            disabled={saving}
            className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
            data-testid="admin-medico-save"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Guardando…
              </>
            ) : (
              "Guardar cambios"
            )}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {k}
      </div>
      <div className="text-sm text-foreground mt-0.5 capitalize">{String(v || "—").replace("_", " ")}</div>
    </div>
  );
}

function StatusPill({ on, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
        on
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
    >
      {on ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
      {children}
    </span>
  );
}

function SuperadminDocBtn({ medicoUserId, docId, tipo }) {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/medicos/${medicoUserId}/documentos/${docId}/url-firmada`,
      );
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
      else toast.error("No se pudo generar la URL.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={open}
      disabled={loading}
      className="text-[#2563EB] border-[#2563EB]/40"
      data-testid={`superadmin-doc-view-${tipo}`}
    >
      {loading ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <ExternalLink size={12} />
      )}
      Ver
    </Button>
  );
}

